import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatRut, validateRut, apiError } from "../lib/format";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import CMFFooter from "../components/CMFFooter";

export default function Register() {
  const [form, setForm] = useState({ name: "", rut: "", phone: "+56 ", email: "", pin: "" });
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onRut = (e) => set("rut", formatRut(e.target.value));
  const onPin = (e) => set("pin", e.target.value.replace(/\D/g, "").slice(0, 4));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || form.name.trim().length < 3) return toast.error("Ingresa tu nombre completo");
    if (!validateRut(form.rut)) return toast.error("RUT chileno inválido");
    if (!form.phone || form.phone.length < 8) return toast.error("Ingresa un teléfono válido");
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) return toast.error("Correo inválido");
    if (form.pin.length !== 4) return toast.error("El PIN debe tener 4 dígitos");
    setBusy(true);
    try {
      await register({ ...form, rut: formatRut(form.rut) });
      toast.success("Cuenta creada. ¡Bienvenido!");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(apiError(err, "No pudimos crear tu cuenta"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <header className="px-6 sm:px-12 lg:px-20 py-8 flex items-center justify-between border-b border-hairline">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo-efe.png" alt="EFE - CAPITAL" className="w-10 h-10 object-contain shrink-0" />
          <div className="leading-tight">
            <div className="font-display font-black text-lg tracking-tight">EFE</div>
            <div className="label-mono">CAPITAL</div>
          </div>
        </Link>
        <Link to="/login" className="text-sm text-inkMuted hover:text-forest flex items-center gap-2" data-testid="back-to-login">
          <ArrowLeft size={16} /> Volver a ingresar
        </Link>
      </header>

      <div className="flex-1 grid lg:grid-cols-5 gap-0">
        <div className="lg:col-span-3 px-6 sm:px-12 lg:px-20 py-12">
          <div className="max-w-lg">
            <div className="label-mono mb-3">02 / Nueva cuenta</div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-3 leading-[0.95]">
              Abre tu cuenta EFE - CAPITAL
            </h1>
            <p className="text-inkMuted mb-10">Solo necesitamos unos datos para comenzar. Todo tu registro está cifrado.</p>

            <form onSubmit={onSubmit} className="space-y-5">
              <Field label="Nombre completo">
                <input data-testid="reg-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="input" placeholder="Ej: Javiera Araya" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="RUT chileno">
                  <input data-testid="reg-rut-input" value={form.rut} onChange={onRut} className="input font-mono" placeholder="12.345.678-9" />
                </Field>
                <Field label="Teléfono">
                  <input data-testid="reg-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input" placeholder="+56 9 1234 5678" />
                </Field>
              </div>
              <Field label="Correo electrónico">
                <input data-testid="reg-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input" placeholder="tu@correo.cl" />
              </Field>
              <Field label="PIN (4 dígitos)">
                <input data-testid="reg-pin-input" type="password" inputMode="numeric" value={form.pin} onChange={onPin} className="input font-mono text-2xl tracking-[0.5em]" placeholder="••••" />
              </Field>

              <button
                type="submit"
                data-testid="reg-submit-btn"
                disabled={busy}
                className="w-full h-14 bg-forest text-bg font-display font-semibold tracking-wide flex items-center justify-center gap-2 hover:bg-forest-hover disabled:opacity-60"
              >
                {busy ? "Creando cuenta…" : "Crear cuenta"}
                <ArrowRight size={18} weight="bold" />
              </button>
            </form>
          </div>
        </div>

        <aside className="hidden lg:block lg:col-span-2 bg-forest text-bg p-12">
          <div className="label-mono text-bg/60 mb-4">// beneficios</div>
          <ul className="space-y-8">
            <li>
              <div className="font-display text-2xl font-bold mb-1">Cuenta sin costo</div>
              <div className="text-bg/70">Abre tu cuenta en segundos. Tu saldo inicia en <span className="font-mono">$ 0</span> y lo construyes tú.</div>
            </li>
            <li>
              <div className="font-display text-2xl font-bold mb-1">Asesor asignado</div>
              <div className="text-bg/70">Conversa por chat con tu asesor de EFE - CAPITAL.</div>
            </li>
            <li>
              <div className="font-display text-2xl font-bold mb-1">Créditos a tu medida</div>
              <div className="text-bg/70">Solicita desde la app y recibe respuesta rápida.</div>
            </li>
          </ul>
        </aside>
      </div>

      <CMFFooter variant="light" />

      <style>{`
        .input {
          width: 100%;
          height: 56px;
          padding: 0 16px;
          border: 1px solid rgba(13,31,22,0.2);
          background: #fff;
          font-size: 16px;
        }
        .input:focus { outline: none; border-color: #0E3B2A; box-shadow: inset 0 0 0 1px #0E3B2A; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-mono block mb-2">{label}</label>
      {children}
    </div>
  );
}
